import { useMemo } from 'react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { CallIntelligenceWetags } from '@wetags/call-intelligence';
import { useCallIntelSubscription, useHasCallIntelAclAccess } from '@frontend/call-intel-core';
import { usePreference } from '@frontend/user-preferences';
import { TERMINAL_ONBOARDING_STATUSES } from '../constant';
import { useBaseOnboardingProgress } from './use-base-onboarding-progress';

export function useOnboardingEligibility() {
  const [lifecycle] = usePreference('CIBaseOnboardingLifecycle');
  const isPermanentlyHidden = !!lifecycle.hiddenAt;

  const { isEnabled: isFeatureEnabled, isLoading: isWetagLoading } = CallIntelligenceWetags.useWetagQuery(
    'call-intel-base-plan-onboarding'
  );

  const { isCallIntelBaseActive, isLoading: isSubscriptionLoading } = useCallIntelSubscription();

  const hasAclAccess = useHasCallIntelAclAccess([Permission.CALL_INTELLIGENCE_READ], { allowDemoOverride: false });

  const basicEligibilityLoading = isWetagLoading || isSubscriptionLoading;

  const passesBasicEligibility =
    !basicEligibilityLoading && !isPermanentlyHidden && !!isFeatureEnabled && !!hasAclAccess && !!isCallIntelBaseActive;

  const {
    onboardingStatus,
    isLoading: isOnboardingStatusLoading,
    hasOnboardingStatusError,
  } = useBaseOnboardingProgress({ enabled: passesBasicEligibility });

  const isLoading = basicEligibilityLoading || (passesBasicEligibility && isOnboardingStatusLoading);

  return useMemo(() => {
    if (isLoading) {
      return { isEligible: false, isLoading: true };
    }

    if (!passesBasicEligibility) {
      return { isEligible: false, isLoading: false };
    }

    if (hasOnboardingStatusError) {
      return { isEligible: false, isLoading: false };
    }

    if (onboardingStatus && TERMINAL_ONBOARDING_STATUSES.includes(onboardingStatus)) {
      return { isEligible: false, isLoading: false };
    }

    return { isEligible: true, isLoading: false };
  }, [isLoading, passesBasicEligibility, hasOnboardingStatusError, onboardingStatus]);
}
